import { type PortableTextBlock } from 'next-sanity'

import { generateElementId } from '@/app/utils/text'

type HeadingItem = {
  text: string
  id: string
  level: string
}

type TableOfContentsProps = {
  allBlocks: PortableTextBlock[]
}

/**
 * Extract text from Portable Text children, handling nested marks and spans
 */
function extractTextFromChildren(children: any[]): string {
  return children
    .map((child: any) => {
      if (child._type === 'span') {
        return child.text || ''
      }
      // Handle nested children if they exist
      if (child.children) {
        return extractTextFromChildren(child.children)
      }
      return ''
    })
    .join('')
    .trim()
}

/**
 * Extract headings from Portable Text blocks
 * Only includes headings that start with a number and dot (e.g., "1. Some Text", "2. Other Text")
 */
function extractHeadings(blocks: PortableTextBlock[]): HeadingItem[] {
  const headings: HeadingItem[] = []
  const headingStyles = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']
  const numberedHeadingPattern = /^\d+\.\s+(.+)$/

  blocks.forEach((block) => {
    if (block._type === 'block' && block.style && headingStyles.includes(block.style)) {
      // Extract text from children, handling nested spans and marks
      const text = block.children ? extractTextFromChildren(block.children) : ''

      // Check if heading starts with number and dot
      const match = text.match(numberedHeadingPattern)
      if (match) {
        const textWithoutNumber = match[1] // Get the captured group (text after "N. ")
        headings.push({
          text: textWithoutNumber,
          id: generateElementId(text), // Use full text for ID to maintain consistency
          level: block.style,
        })
      }
    }
  })

  return headings
}

export function TableOfContents({ allBlocks }: TableOfContentsProps) {
  const headings = extractHeadings(allBlocks)

  if (headings.length === 0) {
    return null
  }

  return (
    <>
      <p className="font-black uppercase size-overline-md lg:size-overline-lg mt-5xl mb-0">
        Table of contents
      </p>
      <nav className="my-3xl lg:my-5xl" aria-label="Table of contents">
        <ol className="flex flex-col gap-md [counter-reset:item] [&_ol]:mt-md [&_ol]:ml-0 md:[&_ol]:ml-md [&_ol]:my-0 [&_ol]:[counter-reset:item]">
          {headings.map((heading, index) => (
            <li
              key={index}
              className="flex gap-sm items-start [counter-increment:item] before:content-[counter(item)'.'] before:text-primary-600 before:font-bold before:min-w-[1.5rem] before:leading-[20px] before:items-start [ol_&]:before:content-[counters(item,'.')'.']"
            >
              <p className="size-body-sm lg:size-body-md mb-0 [&_a]:font-medium [&_a]:underline">
                <a href={`#${heading.id}`}>{heading.text}</a>
              </p>
            </li>
          ))}
        </ol>
      </nav>
    </>
  )
}
